import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HubConnection, HubConnectionState } from '@microsoft/signalr';
import { 
  setAdminApiKey,
  getAdminApiKey,
  hasAdminApiKey,
  createAdminHubConnection,
  getAdminHubUrlCandidates,
  authenticateAdmin,
  adminGetAllPlayers,
  adminGetPlayerSave,
  adminUpdatePlayerSave,
  type PlayerSummary,
  type AdminSaveDto
} from '../../services/adminService';
import './AdminPage.css';

const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const [connection, setConnection] = useState<HubConnection | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(hasAdminApiKey());
  const [apiKey, setApiKey] = useState(getAdminApiKey() || '');
  const [players, setPlayers] = useState<PlayerSummary[]>([]);
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);
  const [gameStateJson, setGameStateJson] = useState('');
  const [status, setStatus] = useState<{ message: string, type: 'info' | 'error' | 'success' }>({ message: '', type: 'info' });
  const [isLoading, setIsLoading] = useState(false);
  const [activeHubUrl, setActiveHubUrl] = useState('');
  const [playerFilter, setPlayerFilter] = useState('');
  const [editorMode, setEditorMode] = useState<'quick' | 'json'>('quick');
  const [parsedGameState, setParsedGameState] = useState<any | null>(null);
  const [structuredError, setStructuredError] = useState<string | null>(null);
  const fallbackUrlsRef = useRef<string[]>([]);
  const connectionUrlRef = useRef<string>('');
  const connectionRef = useRef<HubConnection | null>(null);
  const autoAuthAttempted = useRef(false);

  const isHubUnauthorizedError = (error: unknown) =>
    error instanceof Error && /unauthorized/i.test(error.message);

  const formatNumberDisplay = (value: number | null | undefined) => {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return '0';
    }
    return Math.max(0, value).toLocaleString('en-US');
  };

  const parseNumberInput = (value: string) => {
    if (!value) return null;
    const normalized = value.replace(/,/g, '');
    const parsed = Number(normalized);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const formatBranchLabel = (branch: any, fallbackIndex: number) => {
    const branchTypeValue = branch?.branchType;
    if (typeof branchTypeValue === 'string' && branchTypeValue.trim().length > 0) {
      return branchTypeValue;
    }
    if (typeof branchTypeValue === 'number') {
      const names = ['Mining', 'Engines', 'Cargo', 'Command', 'Shields', 'Hull'];
      return names[branchTypeValue] ?? `Branch ${fallbackIndex + 1}`;
    }
    if (branch?.description) {
      return branch.description;
    }
    return `Branch ${fallbackIndex + 1}`;
  };

  // Authenticate with API key
  useEffect(() => {
    const candidates = getAdminHubUrlCandidates();
    if (candidates.length === 0) {
      setStatus({ message: 'No admin hub endpoints configured', type: 'error' });
      return;
    }

    fallbackUrlsRef.current = candidates.slice(1);
    const initialUrl = candidates[0];
    connectionUrlRef.current = initialUrl;
    setActiveHubUrl(initialUrl);
    const hub = createAdminHubConnection(initialUrl);
    connectionRef.current = hub;
    setConnection(hub);

    return () => {
      connectionRef.current?.stop().catch(() => { /* noop */ });
    };
  }, []);

  useEffect(() => {
    connectionRef.current = connection;
  }, [connection]);

  const isNegotiationNotFoundError = (error: unknown) => {
    if (!(error instanceof Error)) return false;
    return (
      /Status code '404'/.test(error.message) ||
      /404/.test(error.message)
    );
  };

  const createConnectionForUrl = (hubUrl: string) => {
    const newHub = createAdminHubConnection(hubUrl);
    connectionUrlRef.current = hubUrl;
    setActiveHubUrl(hubUrl);
    connectionRef.current = newHub;
    setConnection(newHub);
    return newHub;
  };

  const tryStartConnection = async (hub: HubConnection): Promise<HubConnection> => {
    try {
      await hub.start();
      return hub;
    } catch (error) {
      if (isNegotiationNotFoundError(error) && fallbackUrlsRef.current.length > 0) {
        const nextUrl = fallbackUrlsRef.current.shift();
        if (nextUrl) {
          console.warn(`[AdminPage] Hub negotiation failed for ${connectionUrlRef.current}. Trying fallback ${nextUrl}`);
          await hub.stop().catch(() => { /* noop */ });
          const nextHub = createConnectionForUrl(nextUrl);
          return tryStartConnection(nextHub);
        }
      }
      throw error;
    }
  };

  const ensureConnected = async () => {
    const currentConnection = connection ?? connectionRef.current;
    if (!currentConnection) {
      throw new Error('Admin hub is still initializing');
    }

    let hub = currentConnection;
    if (hub.state === HubConnectionState.Disconnected) {
      hub = await tryStartConnection(hub);
    }
    return hub;
  };

  const deepClone = <T,>(value: T): T => JSON.parse(JSON.stringify(value));

  const syncParsedState = (json: string) => {
    try {
      const parsed = JSON.parse(json);
      setParsedGameState(parsed);
      setStructuredError(null);
    } catch (error) {
      setStructuredError('Quick controls paused: JSON is invalid.');
    }
  };

  const handleJsonInputChange = (value: string) => {
    setGameStateJson(value);
    syncParsedState(value);
  };

  const applyGameStateUpdate = (updater: (draft: any) => void) => {
    setParsedGameState((previous) => {
      if (!previous) {
        setStatus({ message: 'Load and parse a player save to use quick controls.', type: 'info' });
        return previous;
      }

      const draft = deepClone(previous);
      updater(draft);
      const nextJson = JSON.stringify(draft, null, 2);
      setGameStateJson(nextJson);
      setStructuredError(null);
      return draft;
    });
  };

  const setBasicOre = (value: number) => {
    applyGameStateUpdate((draft) => {
      draft.resources ??= {};
      draft.resources.basicOre = Math.max(0, value);
    });
  };

  const setPrestigeValue = (key: 'prestigeLevel' | 'currentPrestigeScore', value: number) => {
    applyGameStateUpdate((draft) => {
      draft.stats ??= {};
      draft.stats[key] = key === 'prestigeLevel' ? Math.max(0, Math.round(value)) : Math.max(0, value);
    });
  };

  const setBranchUnlockCount = (target: 'mothership' | 'fleet', targetIndex: number, branchIndex: number, unlocked: number) => {
    applyGameStateUpdate((draft) => {
      const vessel = target === 'mothership'
        ? draft.mothership
        : draft.fleet?.[targetIndex];

      const branch = vessel?.upgradeTree?.branches?.[branchIndex];
      if (!branch?.upgrades) {
        return;
      }

      const max = branch.upgrades.length;
      const capped = Math.max(0, Math.min(max, unlocked));
      branch.upgrades.forEach((upgrade: any, idx: number) => {
        upgrade.isUnlocked = idx < capped;
      });
    });
  };

  const getBranchUnlockedCount = (branch: any) => {
    if (!branch?.upgrades) return 0;
    return branch.upgrades.filter((upgrade: any) => upgrade?.isUnlocked).length;
  };

  const handleAuthenticate = async () => {
    if (!apiKey) {
      setStatus({ message: 'Please enter an API key', type: 'error' });
      return;
    }
    setIsLoading(true);
    try {
      const hub = await ensureConnected();
      await authenticateAdmin(hub, apiKey);
      const playersList = await adminGetAllPlayers(hub);
      setPlayers(playersList);
      setIsAuthenticated(true);
      autoAuthAttempted.current = true;
      setStatus({ message: 'Authentication successful', type: 'success' });
    } catch (error) {
      console.error('Authentication failed:', error);
      setStatus({ message: `Authentication failed: ${error instanceof Error ? error.message : 'Unknown error'}`, type: 'error' });
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const resetAdminState = (statusUpdate: { message: string; type: 'info' | 'error' | 'success' }) => {
    setAdminApiKey('');
    setApiKey('');
    setIsAuthenticated(false);
    setPlayers([]);
    setSelectedPlayerId(null);
    setGameStateJson('');
    setParsedGameState(null);
    setStructuredError(null);
    setPlayerFilter('');
    setEditorMode('quick');
    autoAuthAttempted.current = false;
    setStatus(statusUpdate);
  };

  // Logout function
  const handleLogout = () => resetAdminState({ message: 'Logged out', type: 'info' });
  const handleUnauthorized = (message?: string) =>
    resetAdminState({ message: message ?? 'Session expired. Please sign in again.', type: 'error' });

  useEffect(() => {
    if (!connection) return;
    const storedKey = getAdminApiKey();
    if (!storedKey || autoAuthAttempted.current) {
      return;
    }

    autoAuthAttempted.current = true;
    (async () => {
      try {
        const hub = await ensureConnected();
        await authenticateAdmin(hub, storedKey);
        setIsAuthenticated(true);
        await loadPlayers();
      } catch (error) {
        console.error('Auto admin authentication failed', error);
        handleUnauthorized('Session expired. Please sign in again.');
      }
    })();
  }, [connection]);

  // Load players when authenticated
  useEffect(() => {
    if (isAuthenticated && connection) {
      loadPlayers();
    }
  }, [isAuthenticated, connection]);

  // Load players list
  const loadPlayers = async () => {
    if (!connection && !connectionRef.current) {
      setStatus({ message: 'Admin hub is still initializing. Please try again in a moment.', type: 'info' });
      return;
    }

    setIsLoading(true);
    try {
      const hub = await ensureConnected();
      const playersList = await adminGetAllPlayers(hub);
      setPlayers(playersList);
      setStatus({ message: `Loaded ${playersList.length} players`, type: 'success' });
    } catch (error) {
      if (isHubUnauthorizedError(error)) {
        handleUnauthorized();
        return;
      }
      console.error('Failed to load players:', error);
      setStatus({ message: `Failed to load players: ${error instanceof Error ? error.message : 'Unknown error'}`, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  // Load a specific player's save
  const handleLoadPlayer = async (playerId: string) => {
    setIsLoading(true);
    try {
      const hub = await ensureConnected();
      const saveData = await adminGetPlayerSave(hub, playerId);
      setGameStateJson(saveData.gameStateJson);
      syncParsedState(saveData.gameStateJson);
      setSelectedPlayerId(playerId);
      setEditorMode('quick');
      setStatus({ message: `Loaded save for player ${playerId}`, type: 'success' });
    } catch (error) {
      if (isHubUnauthorizedError(error)) {
        handleUnauthorized();
        return;
      }
      console.error(`Failed to load player ${playerId}:`, error);
      setStatus({ message: `Failed to load player: ${error instanceof Error ? error.message : 'Unknown error'}`, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  // Format the JSON for display
  const handleFormatJson = () => {
    try {
      const obj = JSON.parse(gameStateJson);
      const formatted = JSON.stringify(obj, null, 2);
      setGameStateJson(formatted);
      syncParsedState(formatted);
      setStatus({ message: 'JSON formatted', type: 'success' });
    } catch (error) {
      setStatus({ message: `Invalid JSON: ${error instanceof Error ? error.message : 'Unknown error'}`, type: 'error' });
    }
  };

  // Validate the JSON
  const handleValidateJson = () => {
    try {
      const parsed = JSON.parse(gameStateJson);
      setParsedGameState(parsed);
      setStructuredError(null);
      setStatus({ message: 'JSON is valid', type: 'success' });
    } catch (error) {
      setStatus({ message: `Invalid JSON: ${error instanceof Error ? error.message : 'Unknown error'}`, type: 'error' });
    }
  };

  // Save changes to the player's save
  const handleSaveChanges = async () => {
    if (!selectedPlayerId) {
      setStatus({ message: 'No player selected', type: 'error' });
      return;
    }

    try {
      // First validate the JSON
      JSON.parse(gameStateJson);
      
      setIsLoading(true);
      const hub = await ensureConnected();
      const dto: AdminSaveDto = {
        playerId: selectedPlayerId,
        gameStateJson,
        lastModified: new Date().toISOString()
      };
      await adminUpdatePlayerSave(hub, dto);
      syncParsedState(gameStateJson);
      setStatus({ message: 'Save updated successfully', type: 'success' });
    } catch (error) {
      if (isHubUnauthorizedError(error)) {
        handleUnauthorized();
        return;
      }
      console.error('Failed to update save:', error);
      setStatus({ message: `Failed to update save: ${error instanceof Error ? error.message : 'Unknown error'}`, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  // Format a number for display
  const formatNumber = (num: number) => {
    if (num === undefined || num === null) return '0';
    
    if (num >= 1e9) return `${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `${(num / 1e6).toFixed(2)}M`;
    if (num >= 1e3) return `${(num / 1e3).toFixed(2)}K`;
    
    return num.toFixed(0);
  };

  // Format a date for display
  const formatDate = (dateString: string) => {
    if (!dateString) return 'Unknown';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatHubTarget = () => {
    if (!activeHubUrl) return '';
    try {
      const parsed = new URL(activeHubUrl);
      return `${parsed.origin}${parsed.pathname}`;
    } catch {
      return activeHubUrl;
    }
  };

  const normalizedFilter = playerFilter.trim().toLowerCase();
  const filteredPlayers = normalizedFilter
    ? players.filter((player) => {
        const haystack = `${player.playerName ?? ''} ${player.playerId}`.toLowerCase();
        return haystack.includes(normalizedFilter);
      })
    : players;

  const mothershipBranches = parsedGameState?.mothership?.upgradeTree?.branches ?? [];
  const fleetShips: any[] = Array.isArray(parsedGameState?.fleet) ? parsedGameState?.fleet : [];
  const quickControlsDisabled = !parsedGameState || !!structuredError;

  return (
    <div className="admin-page">
      <div className="admin-shell">
        <header className="admin-header">
          <div>
            <p className="admin-subtitle">Fleet Operations Console</p>
            <h1>IdleFleet Admin Interface</h1>
          </div>
          <div className="admin-header-actions">
            {activeHubUrl && (
              <span className="admin-connection-indicator">
                Hub: {formatHubTarget()}
              </span>
            )}
            <button
              className="admin-button admin-button--secondary"
              onClick={() => navigate('/')}
              type="button"
            >
              Back to Game
            </button>
          </div>
        </header>

        {!isAuthenticated ? (
          <div className="admin-card admin-card--auth">
            <div>
              <p className="admin-subtitle">Restricted Area</p>
              <h2>Administrator Access</h2>
              <p className="admin-description">
                Use the secret key configured on the server to unlock diagnostics, fleet editing tools, and manual save import/export.
              </p>
            </div>
            <div className="admin-auth-grid">
              <div className="admin-auth-field">
                <label htmlFor="api-key" className="admin-label">Admin password</label>
                <input
                  id="api-key"
                  className="admin-input"
                  type="password"
                  autoComplete="current-password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="Enter password"
                />
              </div>
              <button
                onClick={handleAuthenticate}
                disabled={isLoading}
                className="admin-button admin-button--accent admin-auth-button"
                type="button"
              >
                {isLoading ? 'Authenticating…' : 'Authenticate'}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="admin-card admin-card--controls">
              <div>
                <p className="admin-label">Players in memory</p>
                <strong className="admin-count">{players.length}</strong>
              </div>
              <div className="admin-control-actions">
                <button
                  onClick={loadPlayers}
                  disabled={isLoading}
                  className="admin-button admin-button--accent"
                  type="button"
                >
                  Refresh Players
                </button>
                <button
                  onClick={handleLogout}
                  className="admin-button admin-button--danger"
                  type="button"
                >
                  Logout
                </button>
              </div>
            </div>

            <div className="admin-layout">
              <section className="admin-card admin-card--list">
                <div className="admin-card-header">
                  <div>
                    <h2>Players</h2>
                    <p className="admin-description">
                      Showing all active players currently resident in the game service memory.
                    </p>
                  </div>
                </div>
                <div className="admin-player-filter">
                  <input
                    className="admin-input admin-player-search"
                    type="text"
                    placeholder="Search by name or ID"
                    value={playerFilter}
                    onChange={(e) => setPlayerFilter(e.target.value)}
                  />
                </div>
                {filteredPlayers.length === 0 ? (
                  <div className="admin-empty-state">
                    {players.length === 0
                      ? 'No players have connected since the server booted.'
                      : 'No players match your filter.'}
                  </div>
                ) : (
                  <div className="admin-player-list">
                    {filteredPlayers.map((player) => (
                      <button
                        key={player.playerId}
                        type="button"
                        onClick={() => handleLoadPlayer(player.playerId)}
                        className={`admin-player-card ${selectedPlayerId === player.playerId ? 'is-active' : ''}`}
                      >
                        <div className="admin-player-heading">
                          <strong>{player.playerName || 'Unnamed Commander'}</strong>
                          <span className="admin-player-pill">
                            Fleet: {player.fleetSize}
                          </span>
                        </div>
                        <div className="admin-player-meta">
                          <span>ID: {player.playerId}</span>
                          <span>Ore: {formatNumber(player.basicOre)}</span>
                        </div>
                        <div className="admin-player-meta">
                          <span>Last Active</span>
                          <span>{formatDate(player.lastSaved)}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </section>

              <section className="admin-card admin-card--editor">
                <div className="admin-card-header admin-card-header--editor">
                  <div>
                    <h2>Game State Editor</h2>
                    <p className="admin-description">
                      Work in a structured view by default, or switch to the raw JSON payload for precise edits.
                    </p>
                  </div>
                  <div className="admin-editor-actions">
                    <div className="admin-editor-mode-toggle">
                      <button
                        type="button"
                        className={`admin-button admin-button--ghost ${editorMode === 'quick' ? 'is-active' : ''}`}
                        onClick={() => setEditorMode('quick')}
                        disabled={editorMode === 'quick'}
                      >
                        Quick Controls
                      </button>
                      <button
                        type="button"
                        className={`admin-button admin-button--ghost ${editorMode === 'json' ? 'is-active' : ''}`}
                        onClick={() => setEditorMode('json')}
                        disabled={!parsedGameState || editorMode === 'json'}
                      >
                        Full JSON
                      </button>
                    </div>
                    <div className="admin-editor-buttons">
                      {editorMode === 'json' && (
                        <>
                          <button
                            onClick={handleFormatJson}
                            disabled={!selectedPlayerId}
                            className="admin-button admin-button--ghost"
                            type="button"
                          >
                            Format JSON
                          </button>
                          <button
                            onClick={handleValidateJson}
                            disabled={!selectedPlayerId}
                            className="admin-button admin-button--ghost"
                            type="button"
                          >
                            Validate JSON
                          </button>
                        </>
                      )}
                      <button
                        onClick={handleSaveChanges}
                        disabled={isLoading || !selectedPlayerId}
                        className="admin-button admin-button--primary"
                        type="button"
                      >
                        {isLoading ? 'Saving…' : 'Save Changes'}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="admin-editor-context">
                  {selectedPlayerId ? (
                    <span>Editing player {selectedPlayerId}</span>
                  ) : (
                    <span>Select a player from the left to view their save data.</span>
                  )}
                </div>
                {editorMode === 'quick' ? (
                  <div className="admin-editor-body">
                    {structuredError && (
                      <div className="admin-structured-warning">{structuredError}</div>
                    )}
                    {!parsedGameState ? (
                      <p className="admin-description">Load a player to enable structured editing tools.</p>
                    ) : (
                      <>
                    <div className="admin-quick-grid">
                      <div className="admin-quick-field">
                        <label className="admin-label" htmlFor="quick-basic-ore">Basic Ore</label>
                        <input
                          id="quick-basic-ore"
                          type="text"
                          inputMode="numeric"
                          className="admin-input admin-number-input"
                          disabled={quickControlsDisabled}
                          value={formatNumberDisplay(parsedGameState?.resources?.basicOre ?? 0)}
                          onChange={(e) => {
                            const numericValue = parseNumberInput(e.target.value);
                            if (numericValue !== null) {
                              setBasicOre(numericValue);
                            }
                          }}
                        />
                      </div>

                      <div className="admin-quick-field">
                        <label className="admin-label" htmlFor="quick-prestige-level">Prestige Level</label>
                        <input
                          id="quick-prestige-level"
                          type="text"
                          inputMode="numeric"
                          className="admin-input admin-number-input"
                          disabled={quickControlsDisabled}
                          value={formatNumberDisplay(parsedGameState?.stats?.prestigeLevel ?? 0)}
                          onChange={(e) => {
                            const numericValue = parseNumberInput(e.target.value);
                            if (numericValue !== null) {
                              setPrestigeValue('prestigeLevel', numericValue);
                            }
                          }}
                        />
                      </div>

                      <div className="admin-quick-field">
                        <label className="admin-label" htmlFor="quick-prestige-score">Prestige Score</label>
                        <input
                          id="quick-prestige-score"
                          type="text"
                          inputMode="numeric"
                          className="admin-input admin-number-input"
                          disabled={quickControlsDisabled}
                          value={formatNumberDisplay(parsedGameState?.stats?.currentPrestigeScore ?? 0)}
                          onChange={(e) => {
                            const numericValue = parseNumberInput(e.target.value);
                            if (numericValue !== null) {
                              setPrestigeValue('currentPrestigeScore', numericValue);
                            }
                          }}
                        />
                      </div>
                    </div>

                        {mothershipBranches.length > 0 && (
                          <div className="admin-upgrade-block">
                            <details className="admin-ship-panel" open>
                              <summary>
                                <strong>Mothership</strong>
                                <span className="admin-player-pill">Core Ship</span>
                              </summary>
                              {mothershipBranches.map((branch: any, branchIndex: number) => {
                                const unlocked = getBranchUnlockedCount(branch);
                                const max = branch?.upgrades?.length ?? 0;
                                const label = formatBranchLabel(branch, branchIndex);
                                return (
                                  <div className="admin-upgrade-row" key={`mothership-${branchIndex}`}>
                                    <div className="admin-upgrade-label">
                                      <span>{label}</span>
                                    </div>
                                    <input
                                      type="number"
                                      className="admin-input admin-number-input"
                                      min={0}
                                      max={max}
                                      disabled={quickControlsDisabled}
                                      value={unlocked}
                                      onChange={(e) => {
                                        const numericValue = Number(e.target.value);
                                        if (!Number.isNaN(numericValue)) {
                                          setBranchUnlockCount('mothership', 0, branchIndex, numericValue);
                                        }
                                      }}
                                    />
                                    <span className="admin-upgrade-progress">/ {max}</span>
                                  </div>
                                );
                              })}
                            </details>
                          </div>
                        )}

                        {fleetShips.length > 0 && (
                          <div className="admin-upgrade-block">
                            <h3>Fleet Upgrades</h3>
                            <div className="admin-ship-upgrades">
                              {fleetShips.map((ship, shipIndex) => (
                                <details className="admin-ship-panel" key={ship.id ?? shipIndex}>
                                  <summary>
                                    <strong>{ship.name || `Ship ${shipIndex + 1}`}</strong>
                                    <span className="admin-player-pill">Type: {ship.type ?? 'Unknown'}</span>
                                  </summary>
                                  {ship?.upgradeTree?.branches?.map((branch: any, branchIndex: number) => {
                                    const unlocked = getBranchUnlockedCount(branch);
                                    const max = branch?.upgrades?.length ?? 0;
                                    const label = formatBranchLabel(branch, branchIndex);
                                    return (
                                      <div className="admin-upgrade-row" key={`${ship.id ?? shipIndex}-${branchIndex}`}>
                                        <div className="admin-upgrade-label">
                                          <span>{label}</span>
                                        </div>
                                        <input
                                          type="number"
                                          className="admin-input admin-number-input"
                                          min={0}
                                          max={max}
                                          disabled={quickControlsDisabled}
                                          value={unlocked}
                                          onChange={(e) => {
                                            const numericValue = Number(e.target.value);
                                            if (!Number.isNaN(numericValue)) {
                                              setBranchUnlockCount('fleet', shipIndex, branchIndex, numericValue);
                                            }
                                          }}
                                        />
                                        <span className="admin-upgrade-progress">/ {max}</span>
                                      </div>
                                    );
                                  })}
                                </details>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ) : (
                  <textarea
                    className="admin-textarea"
                    value={gameStateJson}
                    onChange={(e) => handleJsonInputChange(e.target.value)}
                    placeholder={selectedPlayerId ? 'Loading player save…' : 'Select a player to populate this editor.'}
                    readOnly={!selectedPlayerId || isLoading}
                  />
                )}
              </section>
            </div>
          </>
        )}

        {status.message && (
          <div className={`admin-status admin-status--${status.type}`}>
            {status.message}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
